/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import clsx from 'clsx';
import React, { FC, useContext, useState } from 'react';

import { CHART_COLORS } from '@futrli/colors';
import { formatCurrency, FormattedMessage, useIntl } from '@futrli/localisation';
import { PerformanceMetricsBalanceSheetRatios } from '@futrli-sdk/predict';

import { UIBarChart } from '../../../../ui/charts/bar-chart/bar-chart';
import { UIDonutChart } from '../../../../ui/charts/donut-chart/donut-chart';
import { PredictManagementContext } from '../../../../predict-management/context/predict-management-context';

import parentStyles from '../performance.scss';
import styles from './top-ratios.scss';

interface RatioDataType {
  name: string;
  ratio: (number | number[])[];
  chartLabels: string[];
  chartTitle: string;
  chartSubTitle: string;
  chartDescription: string;
  chartType: 'bar' | 'pie' | 'donut';
  chartColors: string[];
  isPercentage?: boolean;
}
interface Props {
  ratios: PerformanceMetricsBalanceSheetRatios;
}
export const TopRatios: FC<Props> = ({ ratios }: Props) => {
  const [selectedChart, setSelectedChart] = useState(0);
  const { currency } = useContext(PredictManagementContext);
  const intl = useIntl();

  const data: RatioDataType[] = [
    {
      name: intl.formatMessage({ id: 'predict.cash_ratio', defaultMessage: 'Cash Ratio' }),
      ratio: [+ratios.cash.numerator, +ratios.cash.denominator],
      chartLabels: [
        intl.formatMessage({ id: 'predict.cash', defaultMessage: 'cash' }),
        intl.formatMessage({
          id: 'financial_fields.category.current_liabilities',
          defaultMessage: 'Current Liabilities'
        })
      ],
      chartTitle: intl.formatMessage({
        id: 'predict.the_cash_ratio',
        defaultMessage: 'The Cash Ratio'
      }),
      chartSubTitle: intl.formatMessage({
        id: 'predict.cash_ratio_formula',
        defaultMessage: 'Cash / Current Liabilities'
      }),
      chartDescription: intl.formatMessage({
        id: 'predict.cash_ratio_formula_description',
        defaultMessage: `This liquidity metric measures business risk by looking at how your cash could be used to meet short term obligations.
A ratio of 2 would mean that current liabilities could be paid off twice with your current cash balance.
A score of 1 is typically considered good for a relevant liquidity measure but there are other factors.
If you have unpaid invoices or stock that can readily be turned into cash, the quick or current ratio may be a better measure of your liquidity.
Long term debt repayments, risk of customers defaulting and highly variable cashflows may mean a score of 1 is not enough.`
      }),
      chartType: 'bar',
      chartColors: [CHART_COLORS.POSITIVE.PRIMARY, CHART_COLORS.NEGATIVE.PRIMARY]
    },
    {
      name: intl.formatMessage({
        id: 'predict.current_ratio',
        defaultMessage: 'Current Ratio'
      }),
      ratio: [+ratios.current.numerator, +ratios.current.denominator],
      chartLabels: [
        intl.formatMessage({ id: 'financial_fields.category.current_assets', defaultMessage: 'Current Assets' }),
        intl.formatMessage({
          id: 'financial_fields.category.current_liabilities',
          defaultMessage: 'Current Liabilities'
        })
      ],
      chartTitle: intl.formatMessage({
        id: 'predict.the_current_ratio',
        defaultMessage: 'The Current Ratio'
      }),
      chartSubTitle: intl.formatMessage({
        id: 'predict.current_ratio_formula',
        defaultMessage: 'Current Assets / Current Liabilities'
      }),
      chartDescription: intl.formatMessage({
        id: 'predict.current_ratio_formula_description',
        defaultMessage: `This liquidity metric measures business risk by looking at how all of your current assets could cover your current liabilites.
A ratio of 2 would mean that current liabilities could be paid off twice given your current asset balance.
A score of 1 is typically considered good for a relevant liquidity measure but there are other factors.
Across different industries, sales and purchases made on credit can differ substantially.
The ratio assumes receivables and stock can be turned into cash in time to repay debt. 
Use the cash ratio for a more conservative measure of liquidity.`
      }),
      chartType: 'donut',
      chartColors: [CHART_COLORS.POSITIVE.PRIMARY, CHART_COLORS.NEGATIVE.PRIMARY]
    },
    {
      name: intl.formatMessage({ id: 'predict.quick_ratio', defaultMessage: 'Quick Ratio' }),
      ratio: [+ratios.quick.numerator, +ratios.quick.denominator],
      chartLabels: [
        intl.formatMessage({
          id: 'predict.bank_accounts_receivable',
          defaultMessage: 'bank/accounts receivable'
        }),
        intl.formatMessage({
          id: 'financial_fields.category.current_liabilities',
          defaultMessage: 'Current Liabilities'
        })
      ],
      chartTitle: intl.formatMessage({ id: 'predict.the_quick_ratio', defaultMessage: 'The Quick Ratio' }),
      chartSubTitle: intl.formatMessage({
        id: 'predict.quick_ratio_formula',
        defaultMessage: '(cash + receivables) / current liabilities'
      }),
      chartDescription: intl.formatMessage({
        id: 'predict.quick_ratio_formula_description',
        defaultMessage: `This short-term liquidity measure looks at your ability to meet short-term obligations with your most liquid assets.
A ratio of 2 would mean that current liabilities could be paid off twice given current cash and receivables balance.
A score of 1 is typically considered good for a relevant liquidity measure but there are other factors.
Cash ratio may be a better measure if your receivable balance is at risk of non payment or not expected to be paid in the short term.
If you have inventory (or any other current asset) that can readily be turned into cash, the current ratio may be more appropriate.`
      }),
      chartType: 'bar',
      chartColors: [CHART_COLORS.POSITIVE.PRIMARY, CHART_COLORS.NEGATIVE.PRIMARY, CHART_COLORS.POSITIVE.SECONDARY]
    },
    {
      name: intl.formatMessage({ id: 'predict.debt_to_equity_ratio', defaultMessage: 'Debt to Equity Ratio' }),
      ratio: [+ratios.debt_to_equity.numerator, +ratios.debt_to_equity.denominator],
      chartLabels: [
        intl.formatMessage({
          id: 'predict.total_liabilities',
          defaultMessage: 'total liabilities'
        }),
        intl.formatMessage({ id: 'financial_fields.category.equity', defaultMessage: 'Equity' })
      ],
      chartTitle: intl.formatMessage({
        id: 'predict.the_debt_to_equity_ratio',
        defaultMessage: 'The Debt to Equity Ratio'
      }),
      chartSubTitle: intl.formatMessage({
        id: 'predict.debt_to_equity_ratio_formula',
        defaultMessage: 'Total Liabilities / Equity'
      }),
      chartDescription: intl.formatMessage({
        id: 'predict.debt_to_equity_ratio_formula_description',
        defaultMessage: `This ratio is primarily concerned with measuring business risk.
It shows proportionately how much equity and debt you are using to finance your assets.
In case of business decline, it indicates how far shareholder's equity will go with fulfilling obligations to creditors.
A ratio of 2 would mean total liabilities are double the value of shareholder equity.
A high score isn't necessarily bad. A young company in a growing market should be leveraging more debt to finance future growth.
In 2017, Netflix's D/E ration was 4.29 compared to Apple's 1.8.`
      }),
      chartType: 'pie',
      chartColors: [CHART_COLORS.NEGATIVE.PRIMARY, CHART_COLORS.POSITIVE.PRIMARY]
    },
    {
      name: intl.formatMessage({ id: 'predict.capital_turnover_ratio', defaultMessage: 'Capital Turnover Ratio' }),
      ratio: [+ratios.captial_turnover.numerator, +ratios.captial_turnover.denominator],
      chartLabels: [
        intl.formatMessage({ id: 'financial_fields.category.sales', defaultMessage: 'Sales' }),
        intl.formatMessage({
          id: 'predict.average_working_capital',
          defaultMessage: 'average working capital'
        })
      ],
      chartTitle: intl.formatMessage({
        id: 'predict.the_capital_turnover_ratio',
        defaultMessage: 'The Capital Turnover Ratio'
      }),
      chartSubTitle: intl.formatMessage({
        id: 'predict.capital_turnover_ratio_formula',
        defaultMessage: 'Sales / Average working capital'
      }),
      chartDescription: intl.formatMessage({
        id: 'predict.capital_turnover_ratio_formula_description',
        defaultMessage: `It measures how effectively you are utilising working capital to generate sales.
Average working capital is your current assets less current liabilities averaged over 12 months.
A lower score would suggest poor sales generation but could indicate bad debts or obsolete inventory.
A higher score is generally better however a very high figure could indicaste additional assets should be acquired to fund growth.
A high level of unpaid bills can artificially inflate this figure.
Fixed assets such as buildings are not included in this measure. If these assets are also driving sales then 'Sales to assets' may be a more appropriate measure.`
      }),
      chartType: 'bar',
      chartColors: [CHART_COLORS.POSITIVE.PRIMARY, CHART_COLORS.POSITIVE.SECONDARY]
    },
    {
      name: intl.formatMessage({ id: 'predict.return_on_assets_ratio', defaultMessage: 'Return on assets ratio' }),
      ratio: [+ratios.return_on_assets.numerator, +ratios.return_on_assets.denominator],
      chartLabels: [
        intl.formatMessage({ id: 'financial_fields.category.profit', defaultMessage: 'Profit' }),
        intl.formatMessage({ id: 'predict.average_total_assets', defaultMessage: 'average total assets' })
      ],
      chartTitle: intl.formatMessage({
        id: 'predict.the_return_on_assets_ratio',
        defaultMessage: 'The Return on Assets Ratio'
      }),
      chartSubTitle: intl.formatMessage({
        id: 'predict.return_on_assets_ratio_formula',
        defaultMessage: 'Net profit / Average total assets x 100'
      }),
      chartDescription: intl.formatMessage({
        id: 'predict.return_on_assets_ratio_description',
        defaultMessage: `It measures how effective you are at generating profit from your assets.
A higher score is a good indication of a productive and efficient company.
Scores over 5% are generally considered good and over 20% excellent but this will vary from industry to industry.
A company building software would have fewer assets on the balance sheet than a manufacturer of physical products and should aim for a higher score.`
      }),
      isPercentage: true,
      chartType: 'bar',
      chartColors: [CHART_COLORS.POSITIVE.PRIMARY, CHART_COLORS.POSITIVE.SECONDARY]
    }
  ];

  const valueOrArrayTotal = (v: number | number[]) => (Array.isArray(v) ? v.reduce((a, c) => c + a) : v);
  const getDatasets = () => {
    const datasets: { data: number[]; colors: string | string[]; barPercentage: number }[] = data[
      selectedChart
    ].ratio.map((_, i) => ({
      data: [],
      colors: i === 0 ? data[selectedChart].chartColors.slice(0, 2) : data[selectedChart].chartColors[2],
      barPercentage: 0.4
    }));
    data[selectedChart].ratio.forEach((ds: number | number[]) => {
      if (Array.isArray(ds)) {
        ds.forEach((n, i) => datasets[i].data.push(n));
      } else {
        datasets[0].data.push(ds);
      }
    });
    return datasets;
  };

  const renderChart = () => {
    switch (data[selectedChart].chartType) {
      case 'donut':
      case 'pie':
        return (
          <UIDonutChart
            labels={data[selectedChart].chartLabels.map(e => e.toUpperCase())}
            datasets={[
              {
                data: data[selectedChart].ratio as number[],
                colors: data[selectedChart].chartColors,
                circumference: 360
              }
            ]}
            graphOptions={{
              cutout: data[selectedChart].chartType === 'donut' ? '80%' : 0
            }}
            labelFormatter={(v, c: { dataIndex: number }) => {
              return `${data[selectedChart].chartLabels[c.dataIndex]}\n${formatCurrency(
                v,
                intl,
                currency || '',
                0,
                0
              )}`;
            }}
          />
        );
      default:
        return (
          <UIBarChart
            testId={`selected-chart-${selectedChart}`}
            labels={data[selectedChart].chartLabels.map(e => e.toUpperCase().split(' '))}
            datasets={
              data[selectedChart].ratio.some(e => Array.isArray(e))
                ? getDatasets()
                : [
                    {
                      data: data[selectedChart].ratio as number[],
                      colors: data[selectedChart].chartColors,
                      barPercentage: 0.4
                    }
                  ]
            }
            dateFormatter={v => v.replace(/,/g, ' ')}
            currencyFormatter={v => formatCurrency(v, intl, currency || '', 0, 0)}
            isStacked={data[selectedChart].ratio.some(e => Array.isArray(e))}
          />
        );
    }
  };
  return (
    <section className={clsx(parentStyles.card)}>
      <h3 className={clsx(parentStyles.cardTitle)}>
        <FormattedMessage id="reporting.ratios" defaultMessage="Ratios" />
      </h3>
      <h4 className={clsx(parentStyles.cardDesc)}>
        <FormattedMessage id="predict.last_month_and_prior_11" defaultMessage="Last month and the prior 11" />
      </h4>
      <div className={clsx(styles.flexWrapper)}>
        <div className={clsx(styles.content)}>
          {data.map((e, i) => (
            <p
              key={i}
              className={clsx(styles.selectable, selectedChart === i && styles.selected)}
              data-testid="select-chart-1"
              onClick={() => setSelectedChart(i)}
            >
              <span className={clsx(styles.value)}>
                {e.isPercentage
                  ? intl.formatNumber(Math.round((100 * valueOrArrayTotal(e.ratio[0])) / valueOrArrayTotal(e.ratio[1])))
                  : intl.formatNumber(Math.round((100 * valueOrArrayTotal(e.ratio[0])) / valueOrArrayTotal(e.ratio[1])) / 100)}
                {e.isPercentage && '%'}
              </span>
              <span className={clsx(styles.label)}>{e.name}</span>
            </p>
          ))}
        </div>
        <div className={clsx(styles.chartSection)}>
          <div className={clsx(styles.chartWrapper)}>{renderChart()}</div>
          <div className={clsx(styles.chartDescription)}>
            <h4 className={clsx('uiTypeFont14Medium')}>{data[selectedChart].chartTitle}</h4>
            <h5 className={clsx('uiTypeFont12Regular')}>{data[selectedChart].chartSubTitle}</h5>
            <p className={clsx('uiTypeFont12Regular')}>{data[selectedChart].chartDescription}</p>
          </div>
        </div>
      </div>
    </section>
  );
};
